import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getLocaleFromPathname, withLocale } from "../lib/locale";
import ChannelChooser from "./ChannelChooser";

const Hero = () => {
    const { pathname } = useLocation();
    const locale = getLocaleFromPathname(pathname);
    const en = locale === "en-US";

    return (
        <section
            data-testid="hero-section"
            className="zild-hero-photo"
            style={{
                position: "relative",
                minHeight: "calc(100svh - 68px)",
                overflow: "hidden",
                backgroundColor: "var(--zild-aubergine-ink)",
            }}
        >
            <picture aria-hidden="true" className="zild-hero-photo__media">
                <source
                    type="image/webp"
                    srcSet="/assets/brand/hero-human-ops-960.webp 960w, /assets/brand/hero-human-ops-1672.webp 1672w"
                    sizes="100vw"
                />
                <img
                    src="/assets/brand/hero-human-ops-960.webp"
                    alt=""
                    width="1672"
                    height="941"
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                />
            </picture>
            <span className="zild-hero-photo__shade" aria-hidden="true" />
            <div className="zild-container zild-hero-photo__container">
                <div className="zild-hero-photo__content zild-reveal">
                    <div className="zild-hero-photo__headline">
                        <h1 data-testid="hero-headline">
                            {en ? "Connect processes, people and tools " : "Integre processos, pessoas e ferramentas "}
                            <span>{en ? "with AI agents to reduce costs." : "com agentes de IA para reduzir custos."}</span>
                        </h1>
                    </div>

                    <div className="zild-hero-photo__aside">
                        <p data-testid="hero-support">
                            {en ? "From administrative work to field operations, turn scattered information into coordinated work. Start with one process, measure the return and expand with security, governance and cost control." : "Do administrativo à operação em campo, transforme informações dispersas em trabalho coordenado. Comece por um processo, meça o retorno e amplie com segurança, governança e controle de custos."}
                        </p>

                        <div className="zild-hero-photo__actions">
                            <Link
                                to={`${withLocale("/contact", locale)}?source=home&intent=ai_adoption_diagnostic`}
                                className="zild-btn zild-btn-primary"
                                data-testid="hero-cta-primary"
                            >
                                Agende seu diagnóstico gratuito
                                <ArrowRight size={16} />
                            </Link>
                            <ChannelChooser className="zild-btn zild-btn-on-dark" />
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .zild-hero-photo__media,
                .zild-hero-photo__shade {
                    position: absolute;
                    inset: 0;
                    pointer-events: none;
                }

                .zild-hero-photo__media {
                    z-index: 0;
                }

                .zild-hero-photo__media img {
                    width: 100%;
                    height: 100%;
                    display: block;
                    object-fit: cover;
                    object-position: center center;
                }

                .zild-hero-photo__shade {
                    z-index: 1;
                    background:
                        linear-gradient(90deg, rgba(27, 20, 16, 0.9) 0%, rgba(16, 43, 48, 0.74) 34%, rgba(16, 43, 48, 0.3) 66%, rgba(27, 20, 16, 0.42) 100%),
                        linear-gradient(0deg, rgba(27, 20, 16, 0.78) 0%, rgba(27, 20, 16, 0.22) 52%, rgba(27, 20, 16, 0.1) 100%);
                }

                .zild-hero-photo__container {
                    min-height: calc(100svh - 68px);
                    display: flex;
                    align-items: center;
                    padding-top: clamp(78px, 9vh, 116px);
                    padding-bottom: clamp(72px, 10vh, 118px);
                    position: relative;
                    z-index: 2;
                }

                .zild-hero-photo__content {
                    width: 100%;
                    display: grid;
                    grid-template-columns: minmax(0, 1.15fr) minmax(320px, 0.64fr);
                    gap: clamp(28px, 6vw, 88px);
                    align-items: center;
                }

                .zild-hero-photo h1 {
                    max-width: 780px;
                    margin: 0;
                    color: var(--zild-text-inverse);
                    font-size: clamp(2.95rem, 4.85vw, 4.95rem);
                    font-weight: 600;
                    line-height: 1.02;
                    letter-spacing: 0;
                    text-wrap: balance;
                }

                .zild-hero-photo h1 span {
                    color: var(--zild-highlight);
                }

                .zild-hero-photo__aside {
                    display: grid;
                    gap: 26px;
                    max-width: 460px;
                    justify-self: end;
                }

                .zild-hero-photo__aside p {
                    margin: 0;
                    color: rgba(255, 255, 255, 0.82);
                    font-size: 17px;
                    line-height: 1.62;
                }

                .zild-hero-photo__actions {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 12px;
                }

                @media (max-width: 980px) {
                    .zild-hero-photo__media img {
                        object-position: 48% center;
                    }

                    .zild-hero-photo__content {
                        grid-template-columns: 1fr;
                        gap: 28px;
                    }

                    .zild-hero-photo__aside {
                        max-width: 620px;
                        justify-self: stretch;
                    }

                    .zild-hero-photo h1 {
                        font-size: clamp(2.55rem, 7.6vw, 4.1rem);
                    }
                }

                @media (max-width: 620px) {
                    .zild-hero-photo {
                        min-height: calc(100svh - 64px) !important;
                    }

                    .zild-hero-photo__media img {
                        object-position: 42% center;
                    }

                    .zild-hero-photo__container {
                        min-height: calc(100svh - 64px);
                        padding-top: 72px;
                        padding-bottom: 44px;
                    }

                    .zild-hero-photo h1 {
                        max-width: 14ch;
                        font-size: clamp(2.2rem, 9vw, 3rem);
                        line-height: 1.04;
                    }

                    .zild-hero-photo__aside {
                        gap: 20px;
                    }

                    .zild-hero-photo__aside p {
                        font-size: 15.5px;
                        line-height: 1.55;
                    }

                    .zild-hero-photo__actions {
                        display: grid;
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </section>
    );
};

export default Hero;
